import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import UploadSubtitle from "../../../components/UploadSubtitle";
import UploadAudio from "../../../components/UploadAudio";
import Crewmembers from "../../../components/Crewmembers";
import {
  ContentTypeBadge,
  EmptyState,
  Page,
  StatusBadge,
} from "../../../components/ui";
import {
  DetailHeader,
  DetailSkeleton,
  useStoredDetail,
} from "../../../components/detail";
import {
  getMovie,
  sendTopicNotification,
  setTrailerMediaId,
  updateMediaInfo,
  updateMovie,
} from "../../../services/content";
import { jwplayerRequest } from "../../../services/jwplayer";
import {
  getMovieGC,
  uploadMedia,
  uploadTrailerMediaId,
} from "../../../store/actions/uploadMediaAction";
import { stripHtml } from "../../../utils/list";
import ContentInfoForm, {
  contentUpdateError,
  exclusivePrice,
  updatedReleaseYear,
} from "../catalogue/ContentInfoForm";
import MediaUploadCard, {
  readVideoDuration,
} from "../catalogue/MediaUploadCard";
import {
  jwMetadata,
  uploadDirect,
  uploadMultipart,
  useDurationCheck,
  waitForJwDuration,
} from "../catalogue/mediaUploads";
import "../catalogue/content-form.css";
import ScheduleRelease from "./ScheduleRelease";

/** Form values of a stored movie. */
export const movieFormValues = (movie) => ({
  title: movie.media?.title || "",
  description: movie.media?.description || "",
  status: Boolean(movie.status),
  language: movie.media?.default_language || "",
  language_code: movie.media?.language_code || "",
  release_date: movie.media?.release_year
    ? new Date(movie.media.release_year).toISOString().split("T")[0]
    : "",
  genres: (movie.genre || []).map((genre) => genre._id),
  seo_tags: movie.media?.seo_tags || [],
  jw_tags: (movie.media?.jw_tags || []).filter((tag) => !tag.endsWith("-movie")),
  content_type: movie.content_type || "default",
  price: movie.price ?? 0,
  monetization: Boolean(movie.media?.monetization),
  availability: movie.availability || "upcoming",
  isShort: Boolean(movie.isShort),
  notify: false,
  spread: false,
  imageBase64: "",
  bannerUrl: movie.thumbnail?.static_thumbnail_url || "",
});

/** Body of the movie update request. */
export const movieUpdatePayload = (values, movie, duration = null) => ({
  thumbnailImageBase64: values.imageBase64,
  description: values.description,
  release_year: updatedReleaseYear(values.release_date, movie.media?.release_year),
  title: values.title,
  status: values.status,
  duration,
  category: "movie",
  availability: values.availability,
  isThumbnailUpdated: Boolean(values.imageBase64),
  genres: values.genres,
  seo_tags: values.seo_tags,
  jw_tags: values.jw_tags,
  content_type: values.content_type,
  monetization: values.monetization,
  isShort: values.isShort,
  mime_type: "video/mp4",
  default_language: values.language,
  language_code: values.language_code,
  price: exclusivePrice(values),
});

/** Validation of the movie update (shared with TV shows and podcasts). */
export const movieUpdateError = contentUpdateError;

/** Push notification announcing a newly published movie. */
const notificationFor = (movie, updated) => ({
  video_id: movie.media.general_content,
  title: updated.isShort ? "Nouveau short ✨" : "Nouvelle video 🔥",
  title_translated: "New Video 🔥",
  description_translated: movie.media.translated_content?.[0]?.title_translated,
  description: movie.media.title,
  thumbnail_url: updated.thumbnail?.static_thumbnail_url,
  notification_thumbnail_url: updated.thumbnail?.real_thumbnail_url,
  real_thumbnail_url: updated.thumbnail?.real_thumbnail_url,
  isShort: updated.isShort,
});

const UpdateMovie = () => {
  const { video_id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { savedMedia, trailerData } = useSelector(
    (state) => state.uploadMediaReducer
  );
  const { item: movie, loading } = useStoredDetail({
    load: () => getMovie(video_id),
    setInfo: getMovieGC,
    storeKey: "generalContentObj",
    pagePath: `/dashboard/update-video/${video_id}`,
  });
  const media = savedMedia?.[0];
  const hasVideo = Boolean(media?.media_id);
  // Duration of the last picked video file, sent with the info update.
  const [duration, setDuration] = useState(null);
  const [formKey, setFormKey] = useState(0);
  // JW id of a video linked before JW knew its real duration.
  const [unverifiedId, setUnverifiedId] = useState(null);
  const checkingDuration = useDurationCheck(
    media?.media_id,
    media?.duration,
    unverifiedId,
    async (jwDuration) => {
      const response = await updateMediaInfo(media._id, {
        duration: jwDuration,
      });
      setUnverifiedId(null);
      dispatch(uploadMedia(response));
    }
  );

  const reload = async () => {
    const data = await getMovie(movie._id);
    dispatch(getMovieGC(data));
    return data;
  };

  const handleUpdate = async (values) => {
    const payload = movieUpdatePayload(values, movie, duration);
    const error = movieUpdateError(payload, Boolean(movie.thumbnail?.static_thumbnail_url));
    if (error) {
      toast.error(t(error));
      return;
    }
    const response = await updateMovie(payload, movie._id);
    if (!response) {
      toast.error(t("Error In General Content"));
      return;
    }
    const data = await reload();
    setFormKey((key) => key + 1);
    toast.success(t("Video Info Updated Successfully!"));

    if (data?.media?.media_id) {
      // Keep the JW metadata in sync with the new info.
      jwplayerRequest({
        body: { metadata: jwMetadata(data.media, "movie", data.genre) },
        method: "patch",
        url: `/media/${data.media.media_id}/`,
      }).catch(() => {});
      if (values.notify && values.status && response.updatedGeneralContent) {
        sendTopicNotification({
          ...notificationFor(data, response.updatedGeneralContent),
          spread: Boolean(values.spread),
        }).catch(() => {});
      }
    }
  };

  const uploadVideo = async (file, report) => {
    const mediaId = await uploadMultipart(
      file,
      jwMetadata(media, "movie", movie.genre),
      report
    );
    report.onProcessing();
    // Short videos are processed within a minute; longer ones are linked with
    // the duration of the local file until JW knows the real one.
    const jwDuration = await waitForJwDuration(mediaId);
    const response = await updateMediaInfo(media._id, {
      media_id: mediaId,
      duration: jwDuration || duration || undefined,
    });
    if (!jwDuration) setUnverifiedId(mediaId);
    dispatch(uploadMedia(response));
  };

  const uploadTrailer = async (file) => {
    const mediaId = await uploadDirect(file, {
      title: media?.title,
      description: stripHtml(media?.description),
    });
    if (!mediaId) throw new Error("Trailer upload failed");
    const response = await setTrailerMediaId(movie._id, {
      trailer_media_id: mediaId,
    });
    dispatch(uploadTrailerMediaId(response.data));
  };

  if (loading) {
    return (
      <Page>
        <DetailSkeleton />
      </Page>
    );
  }
  if (!movie) {
    return (
      <Page>
        <DetailHeader title={t("Edit Video")} />
        <EmptyState icon="ri-film-line">
          {t("No data found for the provided ID!")}
        </EmptyState>
      </Page>
    );
  }

  return (
    <Page>
      <DetailHeader
        title={movie.media?.title || t("Edit Video")}
        badges={
          <>
            <StatusBadge active={Boolean(movie.status)} />
            <ContentTypeBadge type={movie.content_type} />
          </>
        }
        actions={
          <>
            {hasVideo && (
              <ScheduleRelease
                video={movie}
                onChange={async () => {
                  // The video is now « Upcoming »: the form shows it
                  await reload();
                  setFormKey((key) => key + 1);
                }}
              />
            )}
            <Link to={`/dashboard/video-info/${movie._id}`} className="ui-btn ui-btn-secondary">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {t("View")}
            </Link>
          </>
        }
      />

      <div className="content-media">
        <MediaUploadCard
          title={t("Video")}
          mediaId={media?.media_id}
          upload={uploadVideo}
          onFileSelected={(file) => readVideoDuration(file).then(setDuration)}
          note={checkingDuration ? t("Checking the video duration with JW Player…") : null}
          successKey="Video Uploaded Successfully!"
          failureKey="Video Uploading Failed!"
        />
        <MediaUploadCard
          title={t("Trailer")}
          mediaId={trailerData?.[0]?.media_id}
          upload={uploadTrailer}
          successKey="Trailer Uploaded Successfully!"
          failureKey="Trailer Uploading Failed!"
        />
      </div>

      <h2 className="content-section-title">{t("General information")}</h2>
      <ContentInfoForm
        key={`${movie._id}-${formKey}`}
        genreType="movies"
        initialValues={movieFormValues(movie)}
        validate={() => null}
        onSubmit={handleUpdate}
        submitLabel={t("Update")}
        cancelPath="/dashboard/video-list"
        canRelease={hasVideo}
        options={[
          { field: "isShort", label: t("Is Short") },
          { field: "notify", label: t("With-Notification") },
          {
            field: "spread",
            label: t("Spread the sending (expected high traffic)"),
            hint: t("Sent in waves every 15 minutes to logged-in users, so the servers are not flooded. Follow or stop the waves from the notification history."),
            showIf: (values) => values.notify,
          },
        ]}
      />

      <div className="content-extras">
        <section className="ui-card content-side-card">
          <h2 className="content-side-title">{t("add subtitle files")}</h2>
          {!hasVideo && <p className="ui-hint">{t("Upload the video first")}</p>}
          <UploadSubtitle
            media_id={media?.media_id}
            mediaObjId={media?._id}
            disableProp={!hasVideo}
            generalContentObject={[movie]}
            addURL={"add-subtitles"}
            deleteURL={"delete-subtitles"}
            getURL={"get-subtitles-by-media"}
          />
        </section>
        <section className="ui-card content-side-card">
          <h2 className="content-side-title">{t("add audio files")}</h2>
          {!hasVideo && <p className="ui-hint">{t("Upload the video first")}</p>}
          <UploadAudio
            media_id={media?.media_id}
            mediaObjId={media?._id}
            disableProp={!hasVideo}
            generalContentObject={[movie]}
            addURL={"add-audio-track"}
            deleteURL={"delete-audio-track"}
            getURL={"get-audio-tracks-by-media"}
          />
        </section>
        <section className="ui-card content-side-card content-extras-full">
          <h2 className="content-side-title">{t("add crew mem")}</h2>
          <Crewmembers
            generalContentObject={[movie]}
            url={"general-content"}
            getContent={getMovie}
            getContentGC={getMovieGC}
          />
        </section>
      </div>
    </Page>
  );
};

export default React.memo(UpdateMovie);
